import { useState, useEffect } from 'react';

import hyTekExport from './lib/HyTekExport';
import omegaExport from './lib/OmegaExport';

import useOmeMeetData from '../../../state/omeMeet/UseOmeMeetData';
import useOmeEntriesExportData from '../../../state/omeEntriesExport/UseOmeEntriesExportData';
import useOmeMeetEntryInfoData from '../../../state/omeMeetEntryInfo/UseOmeMeetEntryInfoData';

import Constants from '@usas/ui-web/utils/Constants';

const INITIAL_VIEW_STATE = {
  checkBoxListItems: []
};

const useExportEntries = () => {
  const { omeMeetState } = useOmeMeetData();
  const { omeEntriesExportState, getOmeEntriesExport } = useOmeEntriesExportData();
  const { omeMeetEntryInfoState, getOmeMeetEntryInfoByOmeMeetId } = useOmeMeetEntryInfoData();
  const [simpleFormState, setSimpleFormState] = useState(getInitialFormState());
  const [simpleErrorState, setSimpleErrorState] = useState({});
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const [uiState, setUiState] = useState({ exportFileCreated: false, fileContent: "" });

  let fileString = "";

  function createOmegaEntriesExportFile() {
    const exportData = omeEntriesExportState.objData === undefined ? [] : omeEntriesExportState.objData;
    fileString = omegaExport.createOmegaOmeExportDoc(exportData);
  }

  function createHyTekEntriesExportFile() {
    const teams = omeEntriesExportState.objData.teams === undefined ? [] : omeEntriesExportState.objData.teams;
    fileString += hyTekExport.createTeams(teams);

    const unattached = omeEntriesExportState.objData.unattachedSwimmers === undefined ? [] : omeEntriesExportState.objData.unattachedSwimmers;
    fileString += hyTekExport.createUnattachedSwimmers(unattached);

    fileString = hyTekExport.buildCreatedByRec(omeEntriesExportState.objData, fileString) + fileString;
  }

  function provideFile() {
    const file = new Blob([uiState.fileContent], { type: 'application/text' });
    const a = document.createElement('a');

    a.href = URL.createObjectURL(file);

    let meetName = omeEntriesExportState.objData.meetName.replace("&", "").replace("'", "").replace("\\", "").replace("/", "").replace(".", "").replace("#", "");
    const meetId = omeMeetState.objData.omeMeetId;

    let fileName = `${meetId}_${meetName}_export.txt`;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(a.href);
  }

  useEffect(() => {
    if (omeEntriesExportState.isObjLoaded === true) {
      simpleFormState.isInternationalMeet === false ? createHyTekEntriesExportFile() : createOmegaEntriesExportFile();
      setUiState({ ...uiState, exportFileCreated: true, fileContent: fileString });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeEntriesExportState]);

  useEffect(() => {
    if (uiState.exportFileCreated === true && uiState.fileContent.length > 0) {
      provideFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState]);

  const onSimpleFormValueChanged = (component, value) => {
    setSimpleFormState({ ...simpleFormState, [component]: value })
  };

  const onSimpleValueTextPairChanged = (newValue, valuePropertyName, newValueLabel, labelPropertyName) => {
    if (simpleFormState[valuePropertyName] !== newValue || simpleFormState[labelPropertyName] !== newValueLabel) {
      setSimpleFormState(simpleFormState => ({
        ...simpleFormState,
        [valuePropertyName]: newValue,
        [labelPropertyName]: newValueLabel
      }));
    }
  };

  const onEntriesExportClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if ((simpleFormState.isInternationalMeet === true && simpleFormState.country !== Constants.DEFAULT_ID) ||
      simpleFormState.isInternationalMeet === false) {
      if (simpleFormState.omeMeetEntryIds?.length > 0) {
        setSimpleErrorState({});
        getOmeEntriesExport(omeMeetState.objData.omeMeetId, simpleFormState.omeMeetEntryIds?.map(x => x.id));
      }
      else {
        if (state.checkBoxListItems?.length > 1) {
          setSimpleErrorState({ omeMeetEntryIds: 'At least one selection is required' });
        }
        else {
          setSimpleErrorState({});
          getOmeEntriesExport(omeMeetState.objData.omeMeetId, [state.checkBoxListItems[0]?.id]);
        }
      }
    }
    else {
      if (simpleFormState.omeMeetEntryIds?.length > 0) {
        setSimpleErrorState({ country: 'A Country is required' });
      }
      else {
        if (state.checkBoxListItems?.length > 1) {
          setSimpleErrorState({ country: 'A Country is required', omeMeetEntryIds: 'At least one selection is required' });
        }
        else {
          setSimpleErrorState({ country: 'A Country is required' });
        }
      }
    }
  };

  useEffect(() => {
    if (omeMeetEntryInfoState.isArrayLoading !== true && omeMeetState.objData.omeMeetId !== omeMeetEntryInfoState.omeMeetId &&
      omeMeetState.objData.omeMeetId > 0) {
      getOmeMeetEntryInfoByOmeMeetId(omeMeetState.objData.omeMeetId, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetEntryInfoState, omeMeetState.objData.omeMeetId])

  useEffect(() => {
    if (omeMeetEntryInfoState.isArrayLoading !== true && omeMeetEntryInfoState.isArrayLoaded === true &&
      omeMeetState.objData.omeMeetId === omeMeetEntryInfoState.omeMeetId) {
      setState({
        ...state,
        checkBoxListItems: omeMeetEntryInfoState.arrayData.filter(x => x.omeMeetEntryId !== Constants.DEFAULT_ID)?.map(x => {
          return {
            id: x.omeMeetEntryId,
            name: x.omeMeetName
          };
        })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetEntryInfoState, omeMeetState.objData.omeMeetId])

  function getInitialFormState() {
    return {
      omeMeetEntryIds: [],
      isInternationalMeet: false,
      country: Constants.DEFAULT_ID,
      countryName: ''
    };
  }

  return {
    state,
    omeMeetState,
    omeMeetEntryInfoState,
    simpleFormState,
    simpleErrorState,
    omeEntriesExportState,
    onEntriesExportClicked,
    onSimpleFormValueChanged,
    onSimpleValueTextPairChanged
  }
}

export default useExportEntries;